/**
 * Tokens e boilerplate do design system.
 *
 * Fonte: `docs/design-system.md`, seções 2 a 6. Os valores estão centralizados aqui para que os
 * testes de conformidade possam assertar contra a mesma constante que o renderizador usa — evitando
 * o caso em que o teste passa porque repete o erro do código.
 */

export const CORES = {
  brandBlue: '#0A2540',
  brandLight: '#143C66',
  brandAccent: '#F97316',
} as const;

/** Subfamílias da Inter exigidas. O markup usa light, medium e extrabold. */
export const PESOS_INTER = '300;400;500;600;700;800' as const;

export const ASSETS = {
  logoEmaster:
    'https://emaster.cloud/wp-content/uploads/sites/10/2025/08/cropped-Logos-eMaster9.png',
  logoAws: 'https://committed.proposta.emaster.info/images/awslogo.webp',
  baseFotos: 'https://committed.proposta.emaster.info/inside_sales/',
} as const;

/** Fallback único e obrigatório para imagem. Nunca usar serviço de placeholder externo. */
export const FALLBACK_IMAGEM = `onerror="this.style.display='none';"`;

export const CONTAINER = 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' as const;

/**
 * Bloco `@media print`.
 *
 * O legado trata impressão em 1 de 11 arquivos, e é a causa principal de PDF inconsistente.
 *
 * A faixa de vencimento **não** recebe `no-print`: precisa aparecer no documento impresso.
 *
 * `table-header-group` no `thead` da tabela de recursos faz o cabeçalho repetir em cada página. Sem
 * isso, uma proposta com muitos recursos AWS gera páginas de tabela sem indicação de coluna, e o
 * leitor não sabe qual número é preço unitário e qual é total — defeito presente no PDF que o
 * Dynamics gera hoje.
 *
 * `orphans` e `widows` evitam linhas solitárias no topo ou rodapé de página. `break-before: page`
 * no investimento garante que a seção de preço sempre começa em página nova, evitando que fique
 * cortada ao meio.
 *
 * `.diagrama-rolavel` desfaz a rolagem horizontal do diagrama de arquitetura. Na tela o diagrama tem
 * largura mínima e rola, porque encolher um desenho de 660 px para a largura de um celular deixaria os
 * rótulos ilegíveis. No papel não existe rolagem: sem esta regra, tudo além da primeira dobra seria
 * simplesmente cortado do PDF.
 */
const CSS_IMPRESSAO = `
        @media print {
            .no-print { display: none !important; }
            body { background-color: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            section { page-break-inside: avoid; break-inside: avoid; }
            .shadow-sm, .shadow-md, .shadow-lg, .shadow-xl, .shadow-2xl { box-shadow: none !important; }
            .tabela-recursos-cabecalho { display: table-header-group; }
            tr { page-break-inside: avoid; break-inside: avoid; }
            h2, h3 { page-break-after: avoid; break-after: avoid; }
            p { orphans: 3; widows: 3; }
            #investimento { page-break-before: always; break-before: page; }
            #contato { page-break-before: always; break-before: page; }
            img { max-height: 200px; }
            .diagrama-rolavel { overflow: visible !important; }
            .diagrama-rolavel > * { min-width: 0 !important; }
            @page { margin: 14mm 12mm; }
        }`;

const CSS_BASE = `
        .glass-card {
            background: rgba(255, 255, 255, 0.95);
            backdrop-filter: blur(10px);
        }
        .hero-pattern {
            background-color: ${CORES.brandBlue};
            background-image: radial-gradient(circle at 100% 100%, ${CORES.brandLight} 0, ${CORES.brandBlue} 50%, ${CORES.brandBlue} 100%);
        }`;

/**
 * Configuração do Tailwind embutida no documento.
 *
 * `brand.gray` é deliberadamente ausente: está declarado em 6 arquivos do legado e não é usado em
 * nenhum (anti-padrão 5).
 */
const CONFIG_TAILWIND = `
        tailwind.config = {
            theme: {
                extend: {
                    fontFamily: { sans: ['Inter', 'sans-serif'] },
                    colors: {
                        brand: {
                            blue: '${CORES.brandBlue}',
                            light: '${CORES.brandLight}',
                            accent: '${CORES.brandAccent}'
                        }
                    }
                }
            }
        }`;

/**
 * Monta o `<head>` do documento.
 *
 * @param titulo Já escapado pelo chamador.
 * @param descricao Já escapada. Usada em `meta description` e Open Graph.
 */
export function cabecalhoDocumento(titulo: string, descricao: string): string {
  return `<!DOCTYPE html>
<html lang="pt-BR" class="scroll-smooth">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="robots" content="noindex, nofollow">
    <base target="_self">
    <link rel="icon" type="image/png" href="https://committed.proposta.emaster.info/images/emaster-favicon.png">
    <meta name="description" content="${descricao}">
    <meta property="og:title" content="${titulo}">
    <meta property="og:description" content="${descricao}">
    <meta property="og:type" content="website">
    <title>${titulo}</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <script src="https://cdn.jsdelivr.net/npm/chart.js"></script>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@${PESOS_INTER}&display=swap" rel="stylesheet">
    <script src="https://unpkg.com/lucide@latest"></script>
    <script>${CONFIG_TAILWIND}
    </script>
    <style>${CSS_BASE}
${CSS_IMPRESSAO}
    </style>
</head>
<body class="bg-gray-50 text-gray-800 antialiased selection:bg-brand-accent selection:text-white font-sans">`;
}

/** Fecha o documento e inicializa os ícones do Lucide. */
export function rodapeDocumento(scriptsExtra: readonly string[] = []): string {
  const extras = scriptsExtra.filter((s) => s.trim() !== '').join('\n');
  return `
    <script>
        lucide.createIcons();
${extras}
    </script>
</body>
</html>
`;
}

/** Ícone do Lucide. Nunca emitir SVG inline (anti-padrão 17). */
export function icone(nome: string, classe = 'w-5 h-5'): string {
  return `<i data-lucide="${nome}" class="${classe}"></i>`;
}
